/**
 * Artwork made in the browser, by the same rules as the media agent (the backend's
 * media/domain/images.ts): crop to the role's shape around the focal point, the role's widths as
 * WebP, the dominant colour and a blurhash. No image service and no quota: the editor's computer
 * does the work.
 */

import { encode as blurhashEncode } from "blurhash";
import { cropFor, ROLES, sourceProblem, widthsFor, type ImageRole } from "@media-domain/images";

export interface MadeImage {
  role: ImageRole;
  files: Array<{ width: number; height: number; blob: Blob }>;
  color: string;
  blurhash: string;
  focal: { x: number; y: number };
  sha256: string;
}

const hex = (n: number) => Math.round(n).toString(16).padStart(2, "0");

async function sha256Hex(data: BufferSource): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function sourceSize(file: File): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return size;
}

export async function makeImage(file: File, role: ImageRole, focal: { x: number; y: number }): Promise<MadeImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    const spec = ROLES[role];
    const crop = cropFor(bitmap.width, bitmap.height, spec.aspect, focal);
    const problem = sourceProblem(role, crop.width);
    if (problem) throw new Error(problem);

    // A small copy: the dominant colour (its average) and the blurhash.
    const tinyW = 32, tinyH = Math.max(1, Math.round((32 * crop.height) / crop.width));
    const tiny = new OffscreenCanvas(tinyW, tinyH).getContext("2d")!;
    tiny.drawImage(bitmap, crop.left, crop.top, crop.width, crop.height, 0, 0, tinyW, tinyH);
    const pixels = tiny.getImageData(0, 0, tinyW, tinyH).data;
    let r = 0, g = 0, b = 0, n = 0;
    for (let i = 0; i < pixels.length; i += 4) { if (pixels[i + 3]! > 32) { r += pixels[i]!; g += pixels[i + 1]!; b += pixels[i + 2]!; n++; } }
    const color = n ? `#${hex(r / n)}${hex(g / n)}${hex(b / n)}` : "#000000";
    const blurhash = blurhashEncode(new Uint8ClampedArray(pixels), tinyW, tinyH, 4, 3);

    const files: MadeImage["files"] = [];
    for (const width of widthsFor(role, crop.width)) {
      const height = Math.round((width * crop.height) / crop.width);
      const canvas = new OffscreenCanvas(width, height);
      const ctx = canvas.getContext("2d")!;
      ctx.imageSmoothingQuality = "high";
      if (!spec.alpha) { ctx.fillStyle = color; ctx.fillRect(0, 0, width, height); }
      ctx.drawImage(bitmap, crop.left, crop.top, crop.width, crop.height, 0, 0, width, height);
      const blob = await canvas.convertToBlob({ type: "image/webp", quality: spec.alpha ? 0.9 : 0.82 });
      if (blob.type !== "image/webp") throw new Error("This browser cannot make WebP images. Use Chrome, Edge or Firefox, or the media agent.");
      files.push({ width, height, blob });
    }
    // The same identity the media agent gives an image: role, focal point and source together.
    const fileHash = await sha256Hex(await file.arrayBuffer());
    const sha256 = await sha256Hex(new TextEncoder().encode(`${role}:${focal.x},${focal.y}:${fileHash}`) as Uint8Array<ArrayBuffer>);
    return { role, files, color, blurhash, focal, sha256 };
  } finally {
    bitmap.close();
  }
}
